import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProjects } from '../services/projectService';
import { extractDataset, getExports, getExportById } from '../services/pipelineService';

const SCOPE_OPTIONS = [
  { value: 'training_eligible', label: 'Training Eligible' },
  { value: 'community_plus_platform', label: 'Community + Platform' },
  { value: 'community_only', label: 'Community Only' },
  { value: 'public', label: 'Public' },
];

const MEMBERSHIP_OPTIONS = [
  { value: 'community_verified', label: 'Community Verified' },
  { value: 'self_declared', label: 'Self-Declared' },
  { value: 'community_pending', label: 'Community Pending' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'anonymous', label: 'Anonymous' },
];

const PipelinePage = () => {
  const { activeTenant } = useAuth();
  const tenantId = activeTenant?._id;

  const [projects, setProjects] = useState([]);
  const [exports, setExports] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [loadingExports, setLoadingExports] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    label: '',
    projectId: '',
    responseScope: ['training_eligible'],
    membershipStatus: [],
  });

  useEffect(() => {
    if (!tenantId) return;
    getProjects(tenantId)
      .then((res) => setProjects(res.data))
      .catch(console.error);
    loadExports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadExports = () => {
    if (!tenantId) return;
    setLoadingExports(true);
    getExports({ tenantId })
      .then((res) => setExports(res.data))
      .catch(console.error)
      .finally(() => setLoadingExports(false));
  };

  const toggleMultiSelect = (field, value) => {
    setForm((prev) => {
      const current = prev[field];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    setError('');
    setExtractResult(null);

    if (!form.label.trim()) {
      setError('Export label is required');
      return;
    }
    if (!form.responseScope.length) {
      setError('Select at least one response scope');
      return;
    }

    setExtracting(true);
    try {
      const payload = {
        tenantId,
        label: form.label.trim(),
        responseScope: form.responseScope,
      };
      if (form.projectId) payload.projectId = form.projectId;
      if (form.membershipStatus.length) payload.membershipStatus = form.membershipStatus;

      const res = await extractDataset(payload);
      setExtractResult(res.data);
      loadExports();
    } catch (err) {
      setError(err.response?.data?.message || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const handleViewExport = async (id) => {
    setLoadingDetail(true);
    setSelectedExport(null);
    try {
      const res = await getExportById(id);
      setSelectedExport(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load export');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownloadJson = () => {
    if (!selectedExport) return;
    const blob = new Blob(
      [JSON.stringify(selectedExport.records, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cip-export-v${selectedExport.version}-${selectedExport.label.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Model Pipeline</h1>
          <p>
            Consent-filtered, de-identified data extraction for analytics and model training.
            Only responses with <strong>allowModelTraining</strong> consent and eligible scope are included.
          </p>
        </div>

        {/* Extraction Form */}
        <section className="card" style={{ maxWidth: '640px' }}>
          <div className="section-header"><h2>New Extraction</h2></div>
          <form onSubmit={handleExtract}>
            <div className="form-group">
              <label>Export Label *</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Q1 2026 Training Set"
              />
            </div>

            <div className="form-group">
              <label>Project (optional — leave blank for all projects)</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>{p.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Response Scope *</label>
              <div className="checkbox-group">
                {SCOPE_OPTIONS.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="checkbox"
                      checked={form.responseScope.includes(opt.value)}
                      onChange={() => toggleMultiSelect('responseScope', opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Membership Status Filter (optional)</label>
              <div className="checkbox-group">
                {MEMBERSHIP_OPTIONS.map((opt) => (
                  <label key={opt.value}>
                    <input
                      type="checkbox"
                      checked={form.membershipStatus.includes(opt.value)}
                      onChange={() => toggleMultiSelect('membershipStatus', opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <button type="submit" disabled={extracting}>
              {extracting ? 'Extracting…' : 'Run Extraction'}
            </button>
          </form>

          {extractResult && (
            <div className="success-msg" style={{ marginTop: '1rem' }}>
              {extractResult.recordCount === 0 ? (
                <p>{extractResult.message}</p>
              ) : (
                <>
                  <strong>Export created:</strong> v{extractResult.version} — {extractResult.label}<br />
                  <strong>Records:</strong> {extractResult.recordCount}<br />
                  <strong>Consent snapshot:</strong>{' '}
                  {extractResult.consentSnapshot.profilesAllowed} of{' '}
                  {extractResult.consentSnapshot.profilesChecked} user profiles consented
                </>
              )}
            </div>
          )}
        </section>

        <hr />

        {/* Export History */}
        <section>
          <div className="section-header"><h2>Export History</h2></div>
          {loadingExports ? (
            <p>Loading…</p>
          ) : exports.length === 0 ? (
            <p>No exports yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Label</th>
                    <th>Records</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {exports.map((ex) => (
                    <tr key={ex._id}>
                      <td><span className="badge">v{ex.version}</span></td>
                      <td>{ex.label}</td>
                      <td>{ex.recordCount}</td>
                      <td>
                        <span className={`badge ${ex.status === 'complete' ? 'badge-green' : ex.status === 'failed' ? 'badge-red' : 'badge-yellow'}`}>
                          {ex.status}
                        </span>
                      </td>
                      <td>{new Date(ex.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button className="btn-sm" onClick={() => handleViewExport(ex._id)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Export Detail Panel */}
        {(loadingDetail || selectedExport) && (
          <section className="card">
            <div className="section-header"><h2>Export Detail</h2></div>
            {loadingDetail ? (
              <p>Loading records…</p>
            ) : selectedExport ? (
              <>
                <p>
                  <strong>v{selectedExport.version}</strong> — {selectedExport.label}
                </p>
                <p style={{ marginTop: '0.5rem' }}>
                  <strong>Records:</strong> {selectedExport.recordCount} &nbsp;&middot;&nbsp;
                  <strong>Status:</strong>{' '}
                  <span className={`badge ${selectedExport.status === 'complete' ? 'badge-green' : 'badge-red'}`}>
                    {selectedExport.status}
                  </span>
                  &nbsp;&middot;&nbsp;
                  <strong>Exported by:</strong> {selectedExport.exportedBy?.email || 'Unknown'}
                </p>
                <p style={{ marginTop: '0.5rem' }}>
                  <strong>Consent:</strong>{' '}
                  {selectedExport.consentSnapshot?.profilesAllowed ?? '—'} of{' '}
                  {selectedExport.consentSnapshot?.profilesChecked ?? '—'} profiles cleared
                </p>
                <p style={{ marginTop: '0.5rem' }}>
                  <strong>Scope:</strong> [{selectedExport.filters?.responseScope?.join(', ')}]
                  {selectedExport.filters?.membershipStatus?.length
                    ? ` · Membership: [${selectedExport.filters.membershipStatus.join(', ')}]`
                    : ''}
                </p>

                <button onClick={handleDownloadJson} style={{ marginTop: '1rem' }}>
                  Download JSON
                </button>

                <details style={{ marginTop: '1rem' }}>
                  <summary>Preview first 5 records</summary>
                  <pre style={{ maxHeight: '400px' }}>
                    {JSON.stringify(
                      Array.isArray(selectedExport.records)
                        ? selectedExport.records.slice(0, 5)
                        : selectedExport.records,
                      null,
                      2
                    )}
                  </pre>
                </details>
              </>
            ) : null}
          </section>
        )}
      </div>
  );
};

export default PipelinePage;
